'use client';

import { PublicPageLayout } from '@letsgo/PublicPageLayout';
import Link from 'next/link';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Link as MuiLink,
} from '@mui/material';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import LinkIcon from '@mui/icons-material/Link';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';

export default function IntegrationsPage() {
  return (
    <PublicPageLayout>
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" fontWeight={700} mb={2} color="text.primary" component="h1">
          Integrations
        </Typography>
        <Typography variant="h6" color="text.secondary" mb={4}>
          Connect the platforms you use with FloAI Studio—social accounts, publishing, and your content calendar—in one
          workspace.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 6 }}>
          <Typography variant="body1" color="text.primary">
            <strong>In the app</strong>, integrations mean linking your accounts (for example Instagram, TikTok, or
            YouTube) so you can plan posts, run tools, and keep analytics in sync. After you sign in, open{' '}
            <strong>Profile</strong> to manage connected accounts and permissions.
          </Typography>
          <Typography variant="body1" color="text.primary">
            <strong>For developers</strong>, programmatic access (REST, API keys, rate limits) is described on our{' '}
            <MuiLink component={Link} href="/developers" underline="hover">
              Developers
            </MuiLink>{' '}
            page—not here. This page is for creators and teams using the product UI.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Card variant="outlined" sx={{ flex: '1 1 260px', maxWidth: 360 }}>
              <CardContent>
                <HubOutlinedIcon color="primary" sx={{ mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Social &amp; publishing
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect networks you post to; use the dashboard calendar and create flows with those accounts in context.
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ flex: '1 1 260px', maxWidth: 360 }}>
              <CardContent>
                <DashboardCustomizeOutlinedIcon color="primary" sx={{ mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Calendar &amp; content
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Schedule and organize content; integrations keep platform context available where the app needs it.
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ flex: '1 1 260px', maxWidth: 360 }}>
              <CardContent>
                <LinkIcon color="primary" sx={{ mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Tools &amp; audit
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Some tools (like Social Media Audit) need live API credentials on the server—see Support or Getting
                  Started if a feature shows a configuration message.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Button component={Link} href="/auth" variant="contained" color="primary" size="large">
            Sign in
          </Button>
          <Button component={Link} href="/dashboard/profile" variant="outlined" color="primary" size="large">
            Connected accounts (dashboard)
          </Button>
          <Button component={Link} href="/developers" variant="outlined" color="inherit" size="large">
            API &amp; Developers
          </Button>
          <Button component={Link} href="/support" variant="text" color="primary" size="large">
            Support &amp; guides
          </Button>
        </Box>
      </Container>
    </PublicPageLayout>
  );
}
